import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminAuth";
import {
  Calendar as CalendarIcon,
  Clock,
  Mail,
  Phone,
  Check,
  X,
  Trash2,
  AtSign,
  User,
  RefreshCw,
} from "lucide-react";

function formatLong(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
function formatFullDate(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function Schedule() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/schedule");
      setSlots(r.data.slots);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (booking_id, status) => {
    try {
      await adminApi.patch(`/admin/schedule/${booking_id}`, { status });
      load();
    } catch {
      alert("Update failed");
    }
  };
  const deleteBooking = async (booking_id) => {
    if (!window.confirm("Remove this booking? The date will free up.")) return;
    try {
      await adminApi.delete(`/admin/schedule/${booking_id}`);
      load();
      setActive(null);
    } catch {
      alert("Delete failed");
    }
  };

  const totalBooked = slots.filter((s) => s.booking).length;
  const confirmed = slots.filter((s) => s.booking?.status === "confirmed").length;
  const pending = slots.filter((s) => s.booking?.status === "pending").length;
  const open = slots.length - totalBooked;

  return (
    <div data-testid="admin-schedule" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-1">
            Season 2 Schedule
          </p>
          <h2 className="font-display text-4xl uppercase leading-none">
            Guest <span className="text-[#E10600]">Calendar</span>
          </h2>
        </div>
        <button
          onClick={load}
          data-testid="schedule-refresh"
          className="flex items-center gap-2 px-3 py-2 border border-[#262626] hover:border-[#1E6FE0] text-xs tracking-[0.15em] uppercase font-bold"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
        <Summary label="Total Slots" value={slots.length} color="#FFFFFF" />
        <Summary label="Booked" value={totalBooked} color="#E10600" />
        <Summary label="Confirmed" value={confirmed} color="#1E6FE0" />
        <Summary label="Open" value={open} color="#C9A24A" />
      </div>

      {/* Calendar grid (chronological cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
        {slots.map((s, i) => {
          const b = s.booking;
          const status = b?.status || "open";
          const accent =
            status === "confirmed"
              ? "#1E6FE0"
              : status === "pending"
              ? "#E10600"
              : status === "cancelled"
              ? "#525252"
              : "#C9A24A";
          return (
            <button
              key={s.date}
              data-testid={`schedule-cell-${i}`}
              onClick={() => setActive(s)}
              className="relative bg-[#0A0A0A] hover:bg-[#121212] p-5 text-left transition-colors group"
            >
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
                  Week {String(i + 1).padStart(2, "0")}
                </p>
                <span
                  className="text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5"
                  style={{ background: accent, color: status === "open" ? "#0A0A0A" : "#FFFFFF" }}
                >
                  {b ? status : "OPEN"}
                </span>
              </div>
              <p className="mt-3 font-display text-2xl uppercase leading-none text-white group-hover:text-[#E10600] transition-colors">
                {formatLong(s.date)}
              </p>
              <p className="mt-2 text-[10px] tracking-[0.15em] uppercase font-mono text-zinc-500 flex items-center gap-1">
                <Clock className="w-3 h-3 text-[#1E6FE0]" />
                7:00 – 9:00 PM EST
              </p>
              {b ? (
                <div className="mt-4 pt-3 border-t border-[#262626] space-y-1">
                  <p className="font-bold text-white text-sm flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-[#E10600]" />
                    {b.full_name}
                  </p>
                  {b.topic && (
                    <p className="text-xs text-zinc-400 line-clamp-2">{b.topic}</p>
                  )}
                </div>
              ) : (
                <p className="mt-4 pt-3 border-t border-[#262626] text-xs text-zinc-500 font-mono">
                  Awaiting booking
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Detail modal */}
      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md p-4 flex items-center justify-center"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-[#0A0A0A] border border-[#E10600] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-10">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0]">
                  Slot Detail
                </p>
                <button
                  onClick={() => setActive(null)}
                  className="w-9 h-9 border border-[#262626] hover:border-[#E10600] flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl uppercase">
                {formatFullDate(active.date)}
              </h3>
              <p className="mt-2 text-sm text-zinc-400 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#E10600]" />
                {active.time}
              </p>

              {active.booking ? (
                <>
                  <div className="mt-8 space-y-4">
                    <Detail icon={User} label="Guest" value={active.booking.full_name} />
                    <Detail icon={Mail} label="Email" value={active.booking.email} href={`mailto:${active.booking.email}`} />
                    {active.booking.phone && (
                      <Detail icon={Phone} label="Phone" value={active.booking.phone} href={`tel:${active.booking.phone}`} />
                    )}
                    {active.booking.social_handle && (
                      <Detail icon={AtSign} label="Social" value={active.booking.social_handle} />
                    )}
                    {active.booking.topic && (
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">Topic</p>
                        <p className="mt-1 text-zinc-200">{active.booking.topic}</p>
                      </div>
                    )}
                    {active.booking.bio && (
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">Bio</p>
                        <p className="mt-1 text-zinc-300 text-sm">{active.booking.bio}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#262626] flex flex-wrap gap-3">
                    {active.booking.status !== "confirmed" && (
                      <button
                        onClick={() => setStatus(active.booking.id, "confirmed")}
                        className="btn-blue"
                      >
                        <Check className="w-4 h-4" />
                        Confirm Booking
                      </button>
                    )}
                    {active.booking.status !== "cancelled" && (
                      <button
                        onClick={() => setStatus(active.booking.id, "cancelled")}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={() => deleteBooking(active.booking.id)}
                      className="btn-secondary border-[#E10600] text-[#E10600]"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </>
              ) : (
                <div className="mt-8 p-6 border border-[#262626] text-center">
                  <CalendarIcon className="w-8 h-8 text-[#C9A24A] mx-auto mb-3" />
                  <p className="font-display text-2xl uppercase">Open Slot</p>
                  <p className="mt-2 text-sm text-zinc-400">
                    Share the public booking link with prospective guests.
                  </p>
                  <a
                    href="/book"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary mt-6"
                  >
                    View Booking Page
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Summary({ label, value, color }) {
  return (
    <div className="bg-[#0A0A0A] p-5">
      <p className="font-display text-4xl uppercase leading-none" style={{ color }}>
        {value}
      </p>
      <p className="mt-2 text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
        {label}
      </p>
    </div>
  );
}

function Detail({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#1E6FE0] mt-1 flex-shrink-0" />
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
          {label}
        </p>
        <p className="mt-0.5 text-white">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:opacity-80">
      {inner}
    </a>
  ) : (
    inner
  );
}
